import { AccountInfo, InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { RpcError } from "grpc-web";
import { jwtDecode } from "jwt-decode";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Cookies from "universal-cookie";
import { LoginResponse, AuthResponse, User as MonoUser } from "../utils/api/protos/messages/auth.message_pb";
import { RefreshToken as MonoRefreshToken } from "../utils/api/protos/messages/auth.message_pb";
import { getCurrentUser, login as loginAPI, loginWithAzureToken } from "../utils/api/auth";
import { EnvKeys, UserRole } from "../utils/consts";
import { getEnvValue } from "../utils/helpers";
import {
	clearAuthState,
	clearStoredSSOLoginHint,
	clearManualSSOLogout as clearManualSSOLogoutFlag,
	hasManualSSOLogout as hasManualSSOLogoutFlag,
	isSSOUser,
	quickLogout,
	refreshTokenWithDedup
} from "../utils/auth";
import { ClaimsCookies, JwtToken, RefreshToken, User } from "../utils/types";

const AUTH_SCOPES = ["openid", "profile", "email"];

const getDefaultDestination = (role?: string): string => {
	return role?.split(",").includes(UserRole.Provider) ? "/attorney/cases" : "/claims";
};

const isStoredProtectedPath = (path: string | null): path is string => {
	return !!path && path !== "/login" && !path.includes("login") && path !== "/index";
};

interface AuthProviderProps {
	children: ReactNode;
}

export interface IAuth {
	getUser: () => User | undefined;
	setUser: (authResponse: LoginResponse) => void;
	hasRole: (roles: UserRole[]) => boolean;
	getCookies: () => ClaimsCookies | undefined;
	removeCookies: () => void;
	handleJwtRefresh: () => Promise<void>;
	loginInternal: (username: string, password: string) => Promise<LoginResponse>;
	startSSOLogin: (email?: string) => Promise<void>;
	completeAzureLogin: (account: AccountInfo) => Promise<LoginResponse>;
	resumeSSOSession: () => Promise<LoginResponse | null>;
	handleAuthRedirect: () => Promise<void>;
	logout: () => Promise<void>;
	consumeStoredDestination: (role?: string) => string;
	hasManualSSOLogout: () => boolean;
	clearManualSSOLogout: () => void;
	isMsalReady: boolean;
	isAuthInteractionInProgress: boolean;
	isSSOLoginInProgress: boolean;
}

const AuthContext = createContext<IAuth | undefined>(undefined);

export const AuthProvider = ({ children }: AuthProviderProps) => {
	const { instance, inProgress } = useMsal();
	const initPromiseRef = useRef<Promise<void> | null>(null);
	const [isMsalReady, setIsMsalReady] = useState(false);

	const cookieKeys: Array<string> = [EnvKeys.JwtCookieKey, EnvKeys.RefreshCookieKey, EnvKeys.UserCookieKey]
		.map((k: string | EnvKeys) => getEnvValue(k as EnvKeys))
		.filter((k: string) => k.length !== 0);

	const getJwtTokenCookie = useCallback((): JwtToken => {
		const cookies = new Cookies(null, { path: "/" });
		return cookies.get(getEnvValue(EnvKeys.JwtCookieKey));
	}, []);

	const setJwtTokenCookie = useCallback((value: string): void => {
		const cookies = new Cookies(null, { path: "/" });
		const decodedJwtToken: JwtToken = jwtDecode(value, { header: false });
		decodedJwtToken.token = value;
		cookies.set(getEnvValue(EnvKeys.JwtCookieKey), decodedJwtToken);
	}, []);

	const getRefreshTokenCookie = useCallback((): RefreshToken => {
		const cookies = new Cookies(null, { path: "/" });
		return cookies.get(getEnvValue(EnvKeys.RefreshCookieKey));
	}, []);

	const setRefreshTokenCookie = useCallback((value: RefreshToken): void => {
		const cookies = new Cookies(null, { path: "/" });
		cookies.set(getEnvValue(EnvKeys.RefreshCookieKey), value);
	}, []);

	const getUserCookie = useCallback((): User => {
		const cookies = new Cookies(null, { path: "/" });
		return cookies.get(getEnvValue(EnvKeys.UserCookieKey));
	}, []);

	const setUserCookie = useCallback((value: User): void => {
		const cookies = new Cookies(null, { path: "/" });
		cookies.set(getEnvValue(EnvKeys.UserCookieKey), value);
	}, []);

	const convertMonoRefreshTokenToClaimsToken = useCallback((monoToken: MonoRefreshToken): RefreshToken => {
		return {
			expires: monoToken.getExpires(),
			issuedAt: monoToken.getIssuedAt(),
			token: monoToken.getToken()
		};
	}, []);

	const convertMonoUserToClaimsUser = useCallback((monoUser: MonoUser): User => {
		return {
			email: monoUser.getEmail(),
			id: monoUser.getId(),
			name: monoUser.getName(),
			username: monoUser.getUsername(),
			role: monoUser.getRole(),
			externalId: monoUser.getExternalId()
		};
	}, []);

	const setCookies = useCallback(
		(user: User, jwtToken: string, refreshToken: RefreshToken): void => {
			setJwtTokenCookie(jwtToken);
			setRefreshTokenCookie(refreshToken);
			setUserCookie(user);
		},
		[setJwtTokenCookie, setRefreshTokenCookie, setUserCookie]
	);

	const consumeStoredDestination = useCallback((role?: string): string => {
		const lastVisitedPage = localStorage.getItem("lastVisitedPage");
		if (isStoredProtectedPath(lastVisitedPage)) {
			localStorage.removeItem("lastVisitedPage");
			return lastVisitedPage;
		}

		return getDefaultDestination(role);
	}, []);

	const ensureMsalInitialized = useCallback(async (): Promise<void> => {
		if (isMsalReady) {
			return;
		}

		if (!initPromiseRef.current) {
			initPromiseRef.current = instance
				.initialize()
				.then(() => {
					setIsMsalReady(true);
				})
				.catch((error) => {
					initPromiseRef.current = null;
					setIsMsalReady(false);
					throw error;
				});
		}

		return initPromiseRef.current;
	}, [instance, isMsalReady]);

	useEffect(() => {
		void ensureMsalInitialized();
	}, [ensureMsalInitialized]);

	const getCookies = useCallback((): ClaimsCookies | undefined => {
		return {
			jwtToken: getJwtTokenCookie(),
			refreshToken: getRefreshTokenCookie(),
			user: getUserCookie()
		};
	}, [getJwtTokenCookie, getRefreshTokenCookie, getUserCookie]);

	const getUser = useCallback((): User | undefined => {
		return getCookies()?.user;
	}, [getCookies]);

	const removeCookies = useCallback((): void => {
		const cookies = new Cookies(null, { path: "/" });
		cookieKeys.forEach((key: string) => cookies.remove(key));
	}, [cookieKeys]);

	const hasRole = useCallback(
		(roles: UserRole[]): boolean => {
			const user = getUser();
			return user?.role?.split(",").some((role) => roles.includes(role as UserRole)) || false;
		},
		[getUser]
	);

	const handleUserFetch = useCallback(async (): Promise<void> => {
		try {
			const response: AuthResponse = await getCurrentUser();
			const foundUser: MonoUser | undefined = response.getUser();
			if (foundUser) {
				setUserCookie(convertMonoUserToClaimsUser(foundUser));
			}
		} catch (err) {
			console.error((err as RpcError).message);
			throw err;
		}
	}, [convertMonoUserToClaimsUser, setUserCookie]);

	const setUser = useCallback(
		(authResponse: LoginResponse): void => {
			const responseUser: MonoUser | undefined = authResponse.getUser();
			const responseRefreshToken: MonoRefreshToken | undefined = authResponse.getRefreshToken();

			if (responseUser && responseRefreshToken) {
				const newUser: User = convertMonoUserToClaimsUser(responseUser);
				const newRefreshToken: RefreshToken = convertMonoRefreshTokenToClaimsToken(responseRefreshToken);

				setCookies(newUser, authResponse.getJwt(), newRefreshToken);
			}
		},
		[convertMonoRefreshTokenToClaimsToken, convertMonoUserToClaimsUser, setCookies]
	);

	const finalizeAzureLogin = useCallback(
		async (idToken: string, account?: AccountInfo | null): Promise<LoginResponse> => {
			if (account) {
				instance.setActiveAccount(account);
			}

			const response = await loginWithAzureToken(idToken);
			setUser(response);
			clearManualSSOLogoutFlag();
			return response;
		},
		[instance, setUser]
	);

	const handleJwtRefresh = useCallback(async (): Promise<void> => {
		const foundUser: User | undefined = getUser();
		const foundRefreshToken: RefreshToken = getRefreshTokenCookie();

		if (!foundUser || !foundRefreshToken) {
			removeCookies();
			throw new Error("User or refresh token not found");
		}

		try {
			await refreshTokenWithDedup(foundUser.id, foundRefreshToken.token);
			await handleUserFetch();
		} catch (err) {
			console.error("Token refresh failed:", err);
			removeCookies();
			throw err;
		}
	}, [getRefreshTokenCookie, getUser, handleUserFetch, removeCookies]);

	const loginInternal = useCallback(
		async (username: string, password: string): Promise<LoginResponse> => {
			const response = await loginAPI(username, password);
			setUser(response);
			clearManualSSOLogoutFlag();
			clearStoredSSOLoginHint();
			return response;
		},
		[setUser]
	);

	const startSSOLogin = useCallback(
		async (email?: string): Promise<void> => {
			await ensureMsalInitialized();

			if (inProgress !== InteractionStatus.None) {
				return;
			}

			try {
				await instance.loginRedirect({
					scopes: AUTH_SCOPES,
					redirectStartPage: window.location.href,
					loginHint: email,
					prompt: "select_account"
				});
			} catch (error) {
				throw error;
			}
		},
		[ensureMsalInitialized, inProgress, instance]
	);

	const completeAzureLogin = useCallback(
		async (account: AccountInfo): Promise<LoginResponse> => {
			await ensureMsalInitialized();

			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: AUTH_SCOPES
			});

			return finalizeAzureLogin(tokenResponse.idToken, tokenResponse.account || account);
		},
		[ensureMsalInitialized, finalizeAzureLogin, instance]
	);

	const resumeSSOSession = useCallback(async (): Promise<LoginResponse | null> => {
		return null;
	}, []);

	const handleAuthRedirect = useCallback(async (): Promise<void> => {
		await ensureMsalInitialized();

		const result = await instance.handleRedirectPromise();
		if (result?.account) {
			if (result.idToken) {
				await finalizeAzureLogin(result.idToken, result.account);
				return;
			}

			await completeAzureLogin(result.account);
			return;
		}

		const fallbackAccount =
			instance.getActiveAccount() ?? (instance.getAllAccounts().length === 1 ? instance.getAllAccounts()[0] : null);
		if (fallbackAccount) {
			await completeAzureLogin(fallbackAccount);
		}
	}, [completeAzureLogin, ensureMsalInitialized, finalizeAzureLogin, instance]);

	const logout = useCallback(async (): Promise<void> => {
		const foundCookies: ClaimsCookies | undefined = getCookies();
		const currentUser: User | undefined = foundCookies?.user;
		const currentRefreshToken = foundCookies?.refreshToken;
		const ssoAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
		const ssoUser = isSSOUser(currentUser?.externalId) || !!ssoAccount;

		if (!currentUser || !currentRefreshToken) {
			quickLogout(ssoUser ? instance : undefined);
			return;
		}

		try {
			await clearAuthState(currentUser.id, currentRefreshToken.token, ssoUser ? instance : undefined);
		} catch (error) {
			quickLogout(ssoUser ? instance : undefined);
			throw error;
		}
	}, [getCookies, instance]);

	const value = useMemo<IAuth>(
		() => ({
			getUser,
			setUser,
			hasRole,
			getCookies,
			removeCookies,
			handleJwtRefresh,
			loginInternal,
			startSSOLogin,
			completeAzureLogin,
			resumeSSOSession,
			handleAuthRedirect,
			logout,
			consumeStoredDestination,
			hasManualSSOLogout: hasManualSSOLogoutFlag,
			clearManualSSOLogout: clearManualSSOLogoutFlag,
			isMsalReady,
			isAuthInteractionInProgress: inProgress !== InteractionStatus.None,
			isSSOLoginInProgress: inProgress === InteractionStatus.Login
		}),
		[
			completeAzureLogin,
			consumeStoredDestination,
			getCookies,
			getUser,
			handleAuthRedirect,
			handleJwtRefresh,
			hasRole,
			inProgress,
			isMsalReady,
			loginInternal,
			logout,
			removeCookies,
			resumeSSOSession,
			setUser,
			startSSOLogin
		]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): IAuth => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within AuthProvider");
	}
	return context;
};
